import React, { useState } from "react";
import { intl } from "LocaleWrapper";

import { Button, Tag, TagSize } from "@cofano/cck";
import { Dialog, DialogActions, DialogContent, DialogTitle, Step, StepLabel, Stepper } from "@mui/material";

import { Language } from "imports/Utils";

import ProductsAdnTable from "view/products/components/table/ProductsAdnTable";
import ProductsIdtfTable from "view/products/components/table/ProductsIdtfTable";
import ProductsNstrTable from "view/products/components/table/ProductsNstrTable";
import ProductAdnDetails from "view/products/components/details/ProductAdnDetails";
import ProductIdtfDetails from "view/products/components/details/ProductIdtfDetails";
import ProductNstrDetails from "view/products/components/details/ProductNstrDetails";
import { ProductTypes } from "view/products/utils/product.types";

import { AdnGoodFragment, IdtfGoodFragment, NstrGoodFragment } from "__generated__/view/products/graphql/product.fragments.generated";

type ProductGoodMap = {
	[ProductTypes.IDTF]: IdtfGoodFragment;
	[ProductTypes.NSTR]: NstrGoodFragment;
	[ProductTypes.ADN]: AdnGoodFragment;
};

type ProductGood = ProductGoodMap[ProductTypes];

type ProductTagProps = {
    [K in ProductTypes]: {
        type: K;
        product: ProductGoodMap[K];
    }
}[ProductTypes];

export const ProductTag = ({ product, type }: ProductTagProps) => {

	const getColor = () => {
		switch (type) {
			case ProductTypes.IDTF: return "#A020F0";
			case ProductTypes.NSTR: return "#00FFFF";
			case ProductTypes.ADN: return "#D18427";
		}
	};

	const getDescription = () => {
		switch (type) {
			case ProductTypes.IDTF:
				return `${(product).idtfNumber}`;
			case ProductTypes.NSTR:
				return `${(product).nstrCode}`;
			case ProductTypes.ADN:
				return `${(product).unNumber}`;
		}
	};

	return (
		<Tag color={getColor()} text={getDescription() || ""} size={TagSize.SMALL} />
	);
};

type ProductPickerDialogProps = {
    [K in ProductTypes]: {
        open: boolean;
        onClose: () => void;
        onChange: (e: ProductGoodMap[K] | undefined) => void;
        type: K;
        list: ProductGoodMap[K][];
        contentLoading?: boolean;
    }
}[ProductTypes];

export const ProductPickerDialog = ({
	open,
	onClose,
	onChange,
	list,
	type,
	contentLoading,
}: ProductPickerDialogProps) => {

	const [ step, setStep ] = useState(0);
	const [ selectedItem, selectItem ] = useState<ProductGood | undefined>(undefined);

	const handleOnClose = () => {
		onClose();
		setStep(0);
		selectItem(undefined);
	};

	const handleOnRowClick = (row: ProductGood) => {
		selectItem(row);
		setStep(1);
	};

	const handleConfirm = () => {
		switch (type) {
			case ProductTypes.IDTF:
				onChange(selectedItem as IdtfGoodFragment);
				break;
			case ProductTypes.NSTR:
				onChange(selectedItem as NstrGoodFragment);
				break;
			case ProductTypes.ADN:
				onChange(selectedItem as AdnGoodFragment);
				break;
		}
		handleOnClose();
	};

	const getTable = () => {
		switch (type) {
			case ProductTypes.IDTF: return <ProductsIdtfTable contentLoading={contentLoading ?? false} content={list} onRowClick={handleOnRowClick} />;
			case ProductTypes.NSTR: return <ProductsNstrTable contentLoading={contentLoading ?? false} content={list} onRowClick={handleOnRowClick} />;
			case ProductTypes.ADN: return <ProductsAdnTable contentLoading={contentLoading ?? false} content={list} onRowClick={handleOnRowClick} />;
		}
	};

	const getDetails = () => {
		switch (type) {
			case ProductTypes.IDTF: return <ProductIdtfDetails idtfGood={selectedItem as IdtfGoodFragment} />;
			case ProductTypes.NSTR: return <ProductNstrDetails nstrGood={selectedItem as NstrGoodFragment} />;
			case ProductTypes.ADN: return <ProductAdnDetails adnGood={selectedItem as AdnGoodFragment} />;
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleOnClose}
			maxWidth="xl"
			fullWidth>
			<DialogTitle>
				{type}
			</DialogTitle>

			<DialogContent style={{ padding: 0 }} dividers>
				<div style={{ padding: 16 }}>
					<Stepper activeStep={step}>
						<Step>
							<StepLabel>{type}</StepLabel>
						</Step>

						<Step>
							<StepLabel>{Language("CONFIRM")}</StepLabel>
						</Step>
					</Stepper>

					<div style={{ marginTop: 16 }}>
						{step === 0 && getTable()}
						{step === 1 && getDetails()}
					</div>
				</div>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleOnClose} variant="text">
					{intl.formatMessage({ id: "CANCEL" })}
				</Button>

				{step === 1 && (
					<React.Fragment>
						<Button
							onClick={() => {
								setStep(0);
								selectItem(undefined);
							}}
							variant="text">
							{intl.formatMessage({ id: "PREVIOUS_PAGE" })}
						</Button>

						<Button
							onClick={() => handleConfirm()}>
							{intl.formatMessage({ id: "CONFIRM" })}
						</Button>
					</React.Fragment>
				)}
			</DialogActions>
		</Dialog>
	);
};
